// SPDX-FileCopyrightText: Szilárd Hajba
// SPDX-License-Identifier: LGPL-3.0-or-later

import { FetchError } from '@cloudillo/core'
import { Alert, Button, IcLoading, IcWarning } from '@cloudillo/react'
import type { TFunction } from 'i18next'
import { useAtom, useAtomValue } from 'jotai'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { activeContextAtom, contextOnboardingAtom, contextRolesAtom } from './atoms'
import { useApiContext } from './hooks'

const POLL_INTERVAL_MS = 60_000
const RESEND_COOLDOWN_MS = 60_000

function formatRemaining(t: TFunction, expiresAt: string): string {
	const ms = new Date(expiresAt).getTime() - Date.now()
	if (ms <= 0) return t('expired')
	const totalMinutes = Math.floor(ms / 60_000)
	const hours = Math.floor(totalMinutes / 60)
	const minutes = totalMinutes % 60
	if (hours > 0) {
		return t('{{hours}}h {{minutes}}m', { hours, minutes })
	}
	return t('{{minutes}}m', { minutes })
}

/**
 * Persistent banner shown across the chrome of a community context whose
 * `ui.onboarding === 'verify-idp'` — i.e. its IDP identity is still in Pending
 * state. Surfaces the auto-deletion deadline and a one-click resend, and polls
 * the IDP status so the banner disappears the moment the activation email is
 * clicked. Renders nothing in any other case (own context, domain-typed
 * community, already-active community).
 */
export function CommunityVerifyIdpBanner() {
	const { t } = useTranslation()
	const [activeContext] = useAtom(activeContextAtom)
	const [contextOnboarding, setContextOnboarding] = useAtom(contextOnboardingAtom)
	const { getClientFor } = useApiContext()
	// Not read directly — it is the re-render signal. `getClientFor` returns null
	// for a foreign idTag until its proxy token is registered, and the token and
	// this atom are written in the same tick, so the effects below re-run the
	// moment a token arrives instead of freezing on the initial null.
	const contextRoles = useAtomValue(contextRolesAtom)

	const [expiresAt, setExpiresAt] = React.useState<string | undefined>()
	const [resendState, setResendState] = React.useState<
		'idle' | 'sending' | 'cooldown' | 'expired'
	>('idle')
	const [, forceTick] = React.useReducer((n: number) => n + 1, 0)
	const cooldownTimerRef = React.useRef<number | undefined>(undefined)

	const idTag = activeContext?.type === 'community' ? activeContext.idTag : undefined
	const isLeader = activeContext?.roles?.includes('leader') ?? false
	const onboarding = idTag ? contextOnboarding[idTag] : undefined
	const visible = isLeader && onboarding === 'verify-idp'

	// 1s ticker for the countdown.
	React.useEffect(() => {
		if (!visible) return
		const id = window.setInterval(forceTick, 1_000)
		return () => window.clearInterval(id)
	}, [visible])

	React.useEffect(() => {
		return () => {
			if (cooldownTimerRef.current !== undefined) {
				window.clearTimeout(cooldownTimerRef.current)
			}
		}
	}, [])

	// Stage A: discover on entry to a community context as a leader.
	// `ui.onboarding` is no longer fetched on context switch (it's per-user,
	// not per-context), so we discover the community's onboarding state via
	// idpStatus(). Runs once per (idTag, leader) combination — if the
	// onboarding value is already known (e.g. from the optimistic write right
	// after community creation), this is a no-op.
	React.useEffect(() => {
		if (!idTag || !isLeader) return
		if (onboarding !== undefined) return
		const contextApi = getClientFor(idTag, { auth: 'required' })
		if (!contextApi) return
		let cancelled = false
		contextApi.profile
			.idpStatus()
			.then((res) => {
				if (cancelled) return
				if (res.expiresAt) setExpiresAt(res.expiresAt)
				setContextOnboarding((prev) => ({
					...prev,
					[idTag]: res.onboarding ?? null
				}))
			})
			.catch((err) => {
				console.warn('community idp-status discovery failed:', err)
			})
		return () => {
			cancelled = true
		}
	}, [idTag, isLeader, onboarding, getClientFor, contextRoles, setContextOnboarding])

	// Stage B: ongoing poll while the banner is up. When status flips to
	// 'active', the backend has already cleared this community's
	// ui.onboarding — reflect that locally so the banner unmounts on next
	// render. Fires an immediate tick on mount so a banner shown from an
	// optimistic onboarding write (community creation) gets a fresh
	// expiresAt without a 60s wait.
	React.useEffect(() => {
		if (!visible || !idTag) return
		const contextApi = getClientFor(idTag, { auth: 'required' })
		if (!contextApi) return
		let cancelled = false
		const tick = async () => {
			try {
				const res = await contextApi.profile.idpStatus()
				if (cancelled) return
				if (res.expiresAt) setExpiresAt(res.expiresAt)
				if (res.status === 'active') {
					setContextOnboarding((prev) => ({
						...prev,
						[idTag]: res.onboarding ?? null
					}))
				}
			} catch (err) {
				console.warn('community idp-status poll failed:', err)
			}
		}
		tick()
		const intervalId = window.setInterval(tick, POLL_INTERVAL_MS)
		return () => {
			cancelled = true
			window.clearInterval(intervalId)
		}
	}, [visible, idTag, getClientFor, contextRoles, setContextOnboarding])

	if (!visible || !idTag) return null

	async function onResend() {
		if (!idTag || resendState !== 'idle') return
		const contextApi = getClientFor(idTag, { auth: 'required' })
		if (!contextApi) return
		setResendState('sending')
		try {
			const res = await contextApi.profile.resendActivation()
			setExpiresAt(res.expiresAt)
			setResendState('cooldown')
			if (cooldownTimerRef.current !== undefined) {
				window.clearTimeout(cooldownTimerRef.current)
			}
			cooldownTimerRef.current = window.setTimeout(() => {
				cooldownTimerRef.current = undefined
				setResendState((s) => (s === 'cooldown' ? 'idle' : s))
			}, RESEND_COOLDOWN_MS)
		} catch (err: unknown) {
			// IDP returns 410 Gone when Identity.expires_at has already passed.
			if (err instanceof FetchError && err.httpStatus === 410) {
				setResendState('expired')
			} else {
				setResendState('idle')
				console.warn('community activation resend failed:', err)
			}
		}
	}

	const expired =
		resendState === 'expired' || (expiresAt && new Date(expiresAt).getTime() <= Date.now())

	return (
		<Alert
			color="warning"
			role="alert"
			className="m-2"
			icon={<IcWarning size={24} />}
			title={
				expired
					? t("This community's identity has expired")
					: expiresAt
						? t(
								"This community's identity will be deleted in {{remaining}} if not activated",
								{ remaining: formatRemaining(t, expiresAt) }
							)
						: t("This community's identity has not been activated yet")
			}
			actions={
				<Button
					color="primary"
					onClick={onResend}
					disabled={Boolean(expired) || resendState !== 'idle'}
				>
					{resendState === 'sending' && <IcLoading className="animate-rotate-cw me-2" />}
					{resendState === 'cooldown' ? t('Email sent') : t('Resend activation')}
				</Button>
			}
		>
			{expired
				? t(
						"It was not activated in time and will not accept new content. Members can still view what's already here."
					)
				: t(
						"An activation email was sent. Until it's confirmed, content posted here may be lost. The deadline doesn't change if you resend."
					)}
		</Alert>
	)
}

// vim: ts=4
